/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { FaCheck, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { RxCross1 } from 'react-icons/rx';
import moment from 'moment';
import { getInvoiceRxCheckView } from '../../../utils/invoiceRxCheckView';

const BADGE_CLASSES = {
    neutral: 'bg-gray-100 text-gray-600',
    success: 'bg-green-100 text-green-700',
    warning: 'bg-yellow-100 text-yellow-700',
    danger: 'bg-red-100 text-red-700',
};

const ROW_STATUS = {
    MATCHED: { label: 'On invoice', className: 'text-green-700', Icon: FaCheck },
    NOT_ON_INVOICE: { label: 'Not on invoice', className: 'text-red-600', Icon: RxCross1 },
    ONLY_ON_INVOICE: { label: 'Only on invoice', className: 'text-gray-500', Icon: null },
};

const EMPTY_CELL = <span className="text-gray-300">—</span>;

// Advisory, read-only view of the invoice-vs-prescription check the API stores on the order
// (additionalDetails.invoiceRxCheck). It never blocks anything on this page.
export default function InvoiceRxCheckPanel({ additionalDetails, onRefresh }) {
    const view = getInvoiceRxCheckView(additionalDetails?.invoiceRxCheck, additionalDetails?.billNumber);
    const hasTable = view.rows.length > 0;

    // Open on a mismatch, closed otherwise; re-applied whenever a new check result arrives,
    // but a pharmacist's own toggle sticks until then.
    const [expanded, setExpanded] = useState(view.defaultExpanded);
    useEffect(() => {
        setExpanded(view.defaultExpanded);
    }, [view.checkedAt, view.defaultExpanded]);

    return (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-soft-xl flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <button
                    type="button"
                    className="flex items-start gap-2 text-left disabled:cursor-default"
                    onClick={() => setExpanded((open) => !open)}
                    disabled={!hasTable}
                    aria-expanded={hasTable ? expanded : undefined}
                >
                    {hasTable && (
                        <span className="mt-1 text-gray-500">
                            {expanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                        </span>
                    )}
                    <span>
                        <span className="block text-base font-semibold text-gray-800">Invoice vs Prescription</span>
                        <span className="block text-xs text-gray-500">
                            Advisory — does not block order flow
                            {view.checkedAt && ` · checked ${moment(view.checkedAt).format('DD MMM YYYY, hh:mm A')}`}
                        </span>
                        {hasTable && (
                            <span className="block text-xs text-gray-600 mt-0.5">
                                Prescription: {view.counts.prescription} · Invoice: {view.counts.invoice} · Matched: {view.counts.matched}
                            </span>
                        )}
                    </span>
                </button>
                <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${BADGE_CLASSES[view.tone]}`}>{view.badge}</span>
                    {onRefresh && (
                        <button type="button" onClick={onRefresh} className="text-xs text-gray-500 underline">
                            Refresh
                        </button>
                    )}
                </div>
            </div>

            {view.isStale && (
                <p className="text-sm text-yellow-700">
                    The bill changed after this check ran, so this result may be out of date.
                </p>
            )}
            {view.message && <p className={`text-sm ${view.state === 'ERROR' ? 'text-yellow-700' : 'text-gray-500'}`}>{view.message}</p>}

            {hasTable && expanded && (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-xs text-gray-500">
                                <th className="py-2 px-3 font-medium w-10">#</th>
                                <th className="py-2 px-3 font-medium">Prescription (OCR)</th>
                                <th className="py-2 px-3 font-medium">Invoice (eVital bill)</th>
                                <th className="py-2 px-3 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {view.rows.map((row, idx) => {
                                const status = ROW_STATUS[row.status];
                                return (
                                    <tr key={`${row.status}-${row.prescription || row.invoice}-${idx}`} className="border-t border-gray-100 align-top">
                                        <td className="py-2 px-3 text-gray-400">{idx + 1}</td>
                                        <td className="py-2 px-3 text-gray-800">{row.prescription || EMPTY_CELL}</td>
                                        <td className="py-2 px-3 text-gray-800">
                                            {row.invoice || EMPTY_CELL}
                                            {row.closest && (
                                                <span className="block text-xs text-gray-500">closest: {row.closest}</span>
                                            )}
                                        </td>
                                        <td className={`py-2 px-3 text-xs font-medium ${status.className}`}>
                                            <span className="inline-flex items-center gap-1">
                                                {status.Icon && <status.Icon size={12} className="flex-shrink-0" />}
                                                {status.label}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
